import { useState } from 'react'

import { Box, Spinner, Text } from '@factorialco/deprecated-design-system'
import {
  DocumentsFileData,
  DocumentsUploadProcessItemFragment,
  DocumentsUploadProcessOperationItemFragment,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'

import PageAssigner from './PageAssigner'

interface Props {
  uploadProcess: DocumentsUploadProcessItemFragment
  fileData: DocumentsFileData
  operations: DocumentsUploadProcessOperationItemFragment[]
  loading: boolean
  onClickPageFile: (fileId: number, page: number) => void
  onFileEmployeesSelected: (employeeId: number, fileId: number, page: number) => void
  onRemoveEmployee: (fileId: number, page: number) => void
  onDiscardFilePage: (fileId: number, page: number) => void
  onEnableFilePage: (fileId: number, page: number) => void
  contractVersions: ContractVersions
}

const FileAssigner = ({
  fileData,
  operations,
  loading = false,
  onClickPageFile,
  onFileEmployeesSelected,
  onRemoveEmployee,
  onDiscardFilePage,
  onEnableFilePage,
  contractVersions,
}: Props) => {
  const [operationsPerPage, setOperationsPerPage] = useState<
    DocumentsUploadProcessOperationItemFragment[][]
  >([])

  useUnsafeEffect(() => {
    if (!operations) {
      return
    }

    setOperationsPerPage(updateOperationsPerPage())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operations])

  const updateOperationsPerPage = (): DocumentsUploadProcessOperationItemFragment[][] => {
    return [...Array(fileData.pages).keys()].map((page) => {
      const operationsPage = operations.filter(
        (operation) => operation.page === page && operation.fileId === fileData.fileId
      )

      if (operationsPerPage[page]?.length !== operationsPage.length) {
        return operationsPage
      }

      return operationsPerPage[page]
    })
  }

  const onEmployeeSelected = useUnsafeCallback(
    (employeeId: number, page: number) => {
      onFileEmployeesSelected(employeeId, fileData.fileId, page)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onFileEmployeesSelected]
  )

  const onClickPage = useUnsafeCallback(
    (page: number) => {
      onClickPageFile(fileData.fileId, page)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onClickPageFile]
  )

  const onRemoveFileEmployee = useUnsafeCallback(
    (page: number) => {
      onRemoveEmployee(fileData.fileId, page)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onRemoveEmployee]
  )

  const onDiscardPage = useUnsafeCallback(
    (page: number) => {
      onDiscardFilePage(fileData.fileId, page)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onDiscardFilePage]
  )

  const onEnablePage = useUnsafeCallback(
    (page: number) => {
      onEnableFilePage(fileData.fileId, page)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onEnableFilePage]
  )

  const showPageNumber = fileData.pages != undefined && fileData.pages > 1

  const pages = useUnsafeMemo(() => {
    return [...Array(fileData.pages).keys()].map((page) => {
      return (
        <PageAssigner
          key={page}
          fileId={fileData.fileId}
          page={page}
          operations={operationsPerPage[page] || []}
          showPageNumber={showPageNumber}
          onClickPage={onClickPage}
          onEmployeeSelected={onEmployeeSelected}
          onRemoveEmployee={onRemoveFileEmployee}
          onDiscardPage={onDiscardPage}
          onEnablePage={onEnablePage}
          contractVersions={contractVersions}
        />
      )
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationsPerPage])

  return (
    <>
      <Box
        key={fileData.fileId}
        paddingTop="s24"
        paddingX="s16"
        paddingBottom="s8"
        background="grey200"
        gap="s8"
        flexDirection="row"
        alignItems="center"
        justifyContent="spaceBetween"
        border={{
          bottom: {
            width: 's1',
            style: 'solid',
            color: 'grey400',
          },
        }}
      >
        <Box flexDirection="column" gap="s8">
          <Text size="100" capitalize weight="bold" caps>
            {fileData.filename}
          </Text>
        </Box>
        {loading && (
          <Box flexDirection="column" gap="s8">
            <Spinner size="s24" color="radicalRed1000" />
          </Box>
        )}
      </Box>
      {!loading && pages}
    </>
  )
}

export default FileAssigner
